import { FetchStatus } from 'servicesCommon/apiTypes';
import {
	isNil,
	lowerCase,
	isEmpty,
	get,
	isUndefined,
	first,
	replace,
	capitalize,
	padStart,
} from 'lodash-es';
import {
	Entity,
	Nullable,
	StatusTable,
	CountriesData,
	CreditCard,
	BookingStatusEnum,
} from 'app-types';
import { DictionaryCurrency } from 'AppGS/api/dictionaries/dictionariesServiceTypes';
import queryString from 'query-string';

export const isLoading = (fetchStatus?: FetchStatus) => fetchStatus === FetchStatus.PENDING;

export const isFetchSuccess = (fetchStatus?: FetchStatus) => fetchStatus === FetchStatus.SUCCESS;

export const isFetchFailure = (fetchStatus?: FetchStatus) => fetchStatus === FetchStatus.FAILURE;

export function hasErrors(fieldsError?: Object) {
	if (!fieldsError) {
		return false;
	}
	return Object.keys(fieldsError).some(field => {
		const value = fieldsError[field];
		if (Array.isArray(value)) {
			return value.some((v: { [x: string]: unknown }) => {
				return Object.keys(v).some(a => v[a]);
			});
		}
		if (typeof value === 'object' && !Array.isArray(value)) {
			return Object.keys(value).some(a => value[a]);
		}
		return value;
	});
}

export function getCountryById(countryId: number, allCountries: Entity[]) {
	return allCountries.find((item: Entity) => item.id === countryId);
}

export function getCityByCountryId(
	countryId: number,
	cityId: number,
	allCities?: { [countryId: string]: Entity[] }
) {
	return allCities && allCities[countryId]
		? allCities[countryId].find((item: Entity) => item.id === cityId)
		: undefined;
}

export const getTextValue = (text: Nullable<string | number>) =>
	isNil(text) || text === '' ? '—' : text;

export const getErrorMsg = (e: Error) => {
	return e.message === 'Network Error'
		? 'Connection error. Please try again or contact Support'
		: e.message;
};

export const pathToArray = (path: string = '') => {
	const sanitizedPath = path.indexOf('/') === 0 ? path.substring(1) : path;
	return sanitizedPath.split('/');
};

export const getDespatchStatusBtnText = (bookingStatus: BookingStatusEnum) => {
	let title = 'Approve';
	if (bookingStatus === BookingStatusEnum.amended) {
		title = 'Confirm amending';
	}
	if (bookingStatus === BookingStatusEnum.cancelled) {
		title = 'Confirm cancellation';
	}

	return title;
};

export const needDespatchRejectBtn = (bookingStatus: BookingStatusEnum) => {
	return bookingStatus === BookingStatusEnum.amended;
};

export function removeEmptyProperties<T extends { [P in keyof T]: T[P] }, TResult = T>(
	payload: T
): TResult {
	return Object.keys(payload).reduce<TResult>(
		(prevPayload, key) => {
			const value = removeEmptyFromArray(payload[key]);
			if (isNil(value)) {
				return {
					...prevPayload,
				};
			}
			if (typeof value === 'string') {
				if (!value.trim()) {
					return {
						...prevPayload,
					};
				}
			}
			return {
				...prevPayload,
				[key]: value,
			};
		},
		{} as TResult
	);
}

export function removeEmptyFromArray<T extends any[], TResult = T>(
	data: T,
	andProperties: boolean = true
): TResult {
	if (!Array.isArray(data)) {
		return data;
	}
	return data.reduce((prev, next) => {
		if (next) {
			const item =
				andProperties && typeof next !== 'string' ? removeEmptyProperties(next) : next;
			if (!isEmpty(item)) {
				return [...prev, item];
			}
		}
		return prev;
	}, []);
}

export function mapEnumToObj<T extends { [P in keyof T]: T[P] }>(
	data: T,
	sep: string = ' '
): Array<{
	id: number;
	name: string;
}> {
	const stringKeys = Object.keys(data).filter(value => {
		const n = Number(value);
		if (isNaN(n)) {
			return value;
		}
		return undefined;
	});

	return stringKeys.map(key => {
		const name = capitalize(replace(lowerCase(key), ' ', sep));
		const id = data[key];
		return {
			id,
			name,
		};
	});
}

export const getCurrencySymbol = (
	currentCurrencyId: number,
	allCurrencies: DictionaryCurrency[]
) => {
	const currentCurrency = allCurrencies.find(item => item.currencyId === currentCurrencyId);

	return currentCurrency && currentCurrency.currencySymbol;
};

export function getStatusType<
	T extends {
		generationError: string | null;
		chargeError?: string;
		isGenerationInProgress: boolean;
	}
>(record?: T): StatusTable {
	if (!record) {
		return undefined;
	}
	const { generationError, chargeError, isGenerationInProgress } = record;
	if (isGenerationInProgress) {
		return 'inProgress';
	}
	if (generationError || chargeError) {
		return 'reGenerate';
	}
	return undefined;
}

export function getClassNameForStatus<
	T extends {
		generationError: string | null;
		isGenerationInProgress: boolean;
	}
>(record?: T) {
	if (!record) {
		return '';
	}
	const status = getStatusType(record);
	switch (status) {
		case 'inProgress':
			return 'inProgress';
		case 'reGenerate':
			return 'reGenerate';
		default:
			return 'download';
	}
}

export const urlFormatter = (baseUrl: string) => {
	return (url: string, params?: object) => {
		let paramsUrl = '';
		if (params) {
			paramsUrl = '?' + queryString.stringify(params);
		}
		return `${baseUrl}/${url}${paramsUrl}`;
	};
};

export function getPhoneCodeByCountryId(
	countries: CountriesData[],
	countryId: number,
	defaultValue: string = ''
) {
	const currentCountry = first(countries.filter(item => item.countryId === countryId));
	return get(currentCountry, 'countryPhoneCode', defaultValue);
}

export function getFrom<T>(data: T, defaultValue?: any) {
	return <K extends keyof T>(key: K, singleDefaultValue?: any): T[K] | undefined => {
		const defValue = isUndefined(singleDefaultValue) ? defaultValue : singleDefaultValue;
		return get(data, key, defValue);
	};
}

export const scrollModalToTop = () => {
	const els = document.getElementsByClassName('ant-modal-wrap');
	if (els.length) {
		els[0].scrollTop = 0;
	}
};

export const getCCDndItems = (activeCreditCards: CreditCard[]) => {
	return !isEmpty(activeCreditCards)
		? activeCreditCards
				.filter(item => item !== undefined)
				.map((_item, index) => {
					return { rowInitialNumber: index };
				})
		: [];
};

export const padStartTwoSymbolsZero = (str: string) => {
	return padStart(str, 2, '0');
};
